import {
  getLeaderboardInstance,
  IIndividualLeaderboardData,
  IInnerTeamLeaderboardData,
  ILeaderboardConfig,
  ILeaderboardData,
  ILeaderboardDataViewModel,
  ILeaderboardInstance,
  ILeaderboardOptInHistory,
  ILeaderboardRanking,
  ILeaderboardRankingDisplayModel,
  ILeaderboardRankingViewModel,
  IStudioLeaderboardViewModel,
  IUserLeaderboardInstance,
  IUserLeaderboardInstanceWrapper,
  IUserLeaderboardPreference,
  IUserLeaderboardPreferenceViewResult,
  IUserLeaderboardViewModel,
  IUserWeightedLeaderboardBreakdown,
  LeaderboardData,
  NotFoundError,
} from 'fitbeat.models';
import moment from 'moment';
import { UserProfile, UserWorkoutSession } from '../../entities';
import {
  DbGymRepository,
  DbLeaderboardConfigRepository,
  IUserProfileRepository,
  IWorkoutRepository,
} from '../../persistence';
import { EnabledFeaturesHydrationService } from '../user/EnabledFeaturesHydrationService';
import {
  getCountTotalMembersPerTeam,
  getCurrentMembersInTeam,
  getDaysIntoChallenge, getDaysOptedIntoTeamLeaderboard,
  getUsersWithActiveLeaderboardPreference,
} from './OptInHelper';

export class ChallengeManager {
  private leaderboardAmountOfOtherTeamMembersToShow = 3;

  private lowerLimitAmountOfIndividualUsersToShow = 20;

  private percentageLimitAmountOfIndividualUsersToShow = 0.5;

  constructor(
    private leaderboardConfigRepository: DbLeaderboardConfigRepository,
    private userProfileRepository: IUserProfileRepository,
    private workoutRepository: IWorkoutRepository,
  ) {}

  public async getLeaderboardsForStudio(date: Date, gymName: string): Promise<IStudioLeaderboardViewModel> {
    const gym = DbGymRepository.getByName(gymName);
    const organisation = EnabledFeaturesHydrationService.getGymOrganisation(gym);
    const leaderboardsConfigsForGym: ILeaderboardConfig[] = await this.leaderboardConfigRepository
      .getApplicableLeaderboardConfigs(date, organisation.name, gym.name, 'active');
    const studioLeaderboards = leaderboardsConfigsForGym
      .filter((config: ILeaderboardConfig) => {
        if (!config.showInStudio) {
          return false;
        }

        if (config.gymName && config.gymName !== gymName) {
          return false;
        }

        return true;
      });

    const primaryLeaderboardConfig = studioLeaderboards[0];
    const secondaryLeaderboardConfig = studioLeaderboards[1];

    let primaryLeaderboardData: ILeaderboardDataViewModel | undefined;
    let secondaryLeaderboardData: ILeaderboardDataViewModel | undefined;

    if (primaryLeaderboardConfig) {
      const primaryLeaderboardInstance = getLeaderboardInstance(primaryLeaderboardConfig, date);
      primaryLeaderboardData = await this.getLeaderboardDataForLeaderboardInstance(
        primaryLeaderboardInstance.leaderboardId,
        new Date(primaryLeaderboardInstance.startDate),
        new Date(primaryLeaderboardInstance.endDate),
      );
    }

    if (secondaryLeaderboardConfig) {
      const secondaryLeaderboardInstance = getLeaderboardInstance(secondaryLeaderboardConfig, date);
      secondaryLeaderboardData = await this.getLeaderboardDataForLeaderboardInstance(
        secondaryLeaderboardInstance.leaderboardId,
        new Date(secondaryLeaderboardInstance.startDate),
        new Date(secondaryLeaderboardInstance.endDate),
      );
    }

    return {
      primaryLeaderboard: primaryLeaderboardData,
      secondaryLeaderboard: secondaryLeaderboardData,
    };
  }

  public async getInactiveLeaderboardsForUser(
    uid: string,
    today: Date,
    viewOption: 'carousel' | 'all',
  ) {
    const user = await this.userProfileRepository.get(uid);
    if (!user) {
      throw NotFoundError.User(uid);
    }

    let leaderboardInstances = await this.getLeaderboardInstancesForUser(
      user,
      today,
      undefined,
      false,
    );

    leaderboardInstances = leaderboardInstances.filter((leaderboard) => !leaderboard.isActive);

    leaderboardInstances = leaderboardInstances.filter((leaderboard) =>
      this.isLeaderboardRelevantForUser(leaderboard, user));

    leaderboardInstances.sort((a, b) => a.config.order - b.config.order);

    const compareFunction = (a: IUserLeaderboardInstanceWrapper, b: IUserLeaderboardInstanceWrapper) => {
      // Ignore the first item
      if (a === leaderboardInstances[0]) { return -1; }
      if (b === leaderboardInstances[0]) { return 1; }

      if (a.endDate.getTime() === b.endDate.getTime()) {
        return a.config.order - b.config.order;
      }

      return b.endDate.getTime() - a.endDate.getTime();
    };
    leaderboardInstances.sort(compareFunction);

    const leaderboardsForUser: IUserLeaderboardViewModel[] = [];
    if (viewOption === 'carousel') {
      leaderboardInstances = leaderboardInstances
        .reduce((acc: IUserLeaderboardInstanceWrapper[], current: IUserLeaderboardInstanceWrapper) => {
          const found = acc.find((item) => item.config.frequency === current.config.frequency);
          if (!found) {
            acc.push(current); // If not found, this is the highest rank for this type so far
          }
          return acc;
        }, []);
    }

    for (const leaderboardInstance of leaderboardInstances) {
      const leaderboardData: ILeaderboardData = await this.getRankedLeaderboardData(
        leaderboardInstance.config,
        getLeaderboardInstance(leaderboardInstance.config, leaderboardInstance.endDate),
      );

      leaderboardsForUser.push(await this.mapLeaderboardDataForUser(
        user,
        leaderboardData,
        leaderboardInstance.config,
        this.isLeaderboardActive(leaderboardData.endDate, today),
        undefined,
      ));
    }
    return leaderboardsForUser;
  }

  public async getLeaderboardsForUser(
    uid: string,
    today: Date,
    fromDate?: Date,
  ): Promise<IUserLeaderboardViewModel[]> {
    const user = await this.userProfileRepository.get(uid);
    if (!user) {
      throw NotFoundError.User(uid);
    }

    const leaderboardInstances = await this.getLeaderboardInstancesForUser(
      user,
      today,
      fromDate,
    );
    const leaderboardsForUser: IUserLeaderboardViewModel[] = [];

    for (const leaderboardInstance of leaderboardInstances) {
      const leaderboardData: ILeaderboardData = await this.getRankedLeaderboardData(
        leaderboardInstance.config,
        getLeaderboardInstance(leaderboardInstance.config, leaderboardInstance.startDate),
      );

      leaderboardsForUser.push(await this.mapLeaderboardDataForUser(
        user,
        leaderboardData,
        leaderboardInstance.config,
        this.isLeaderboardActive(leaderboardData.endDate, today),
        undefined,
      ));
    }

    return leaderboardsForUser;
  }

  public async getLeaderboardInstancesForUser(
    user: UserProfile,
    today: Date,
    fromDate?: Date,
    activeLeaderboards = true,
  ): Promise<IUserLeaderboardInstanceWrapper[]> {
    if (!user.gymName) {
      throw new NotFoundError('gymName', user.uid);
    }

    const leaderboardInstancesFromGymState: IUserLeaderboardInstance[] = await this.workoutRepository
      .getLeaderboardsForUser(user.uid, fromDate);

    const activeLeaderboardConfigs = activeLeaderboards
      ? await this.getActiveAndUpcomingLeaderboardConfigs(
        user.gymName,
        today,
      )
      : await this.getAllLeaderboardConfigs(
        user.gymName,
        today,
      );

    const mapLeaderboardConfigsToInstances = () => activeLeaderboardConfigs
      .map((leaderboardConfig: ILeaderboardConfig): IUserLeaderboardInstanceWrapper => {
        const instance = getLeaderboardInstance(leaderboardConfig, today, activeLeaderboards);

        const isActive = this.isLeaderboardActive(new Date(instance.endDate), today);
        return {
          requireOptIn: this.calculateIfUserHasToOptedIn(leaderboardConfig, user, isActive),
          startDate: new Date(instance.startDate),
          endDate: new Date(instance.endDate),
          leaderboardId: leaderboardConfig._id,
          config: leaderboardConfig,
          isActive,
        };
      });

    const mapGymStateInstances = async (): Promise<IUserLeaderboardInstanceWrapper[]> => {
      const configPromises = leaderboardInstancesFromGymState.map(
        async (entryId) => {
          try {
            return await this.leaderboardConfigRepository.get(entryId.leaderboardId);
          } catch (error) {
            // TODO: Add a logger to the challengeManager?
            return null;
          }
        },
      );
      const configs = await Promise.all(configPromises);

      const validConfigs = configs
        .filter((config) => config !== null)
        .map((config) => config as ILeaderboardConfig);

      return leaderboardInstancesFromGymState
        .map((leaderboardInstance: IUserLeaderboardInstance, index: number):
        IUserLeaderboardInstanceWrapper => ({
          requireOptIn: false,
          startDate: new Date(leaderboardInstance.startDate),
          endDate: new Date(leaderboardInstance.endDate),
          leaderboardId: leaderboardInstance.leaderboardId,
          config: validConfigs[index],
          isActive: new Date(leaderboardInstance.endDate) > today,
        }));
    };

    const mappedActiveLeaderboardInstances = mapLeaderboardConfigsToInstances();
    const gymStateInstances = await mapGymStateInstances();
    const leaderboardInstances: IUserLeaderboardInstanceWrapper[] = mappedActiveLeaderboardInstances
      .concat(gymStateInstances);

    const getUniqueLeaderboardInstances = () => leaderboardInstances
      .filter((value: IUserLeaderboardInstanceWrapper, index: number, self: IUserLeaderboardInstance[]) =>
        value.config !== undefined && self.findIndex(
          (item: IUserLeaderboardInstance) =>
            item.startDate.getTime() === value.startDate.getTime()
            && item.leaderboardId === value.leaderboardId,
        ) === index);

    const uniqueLeaderboardInstances = getUniqueLeaderboardInstances();
    const orderedLeaderboardInstances = this.orderUserLeaderboardInstances(uniqueLeaderboardInstances);

    return orderedLeaderboardInstances;
  }

  public async getPrimaryLeaderboardForUser(
    uid: string,
    date: Date,
    useDeprecatedMethodOfReturningAllRankings = false, // TODO: remove
  ): Promise<IUserLeaderboardViewModel | undefined> {
    const user = await this.userProfileRepository.get(uid);
    if (!user) {
      throw NotFoundError.User(uid);
    }

    const leaderboardInstances = await this.getLeaderboardInstancesForUser(
      user,
      date,
      undefined,
    );

    const featuredLeaderboard = leaderboardInstances[0];

    if (featuredLeaderboard) {
      const leaderboardData: ILeaderboardData = await this.getRankedLeaderboardData(
        featuredLeaderboard.config,
        getLeaderboardInstance(featuredLeaderboard.config, featuredLeaderboard.startDate),
      );

      return this.mapLeaderboardDataForUser(
        user,
        leaderboardData,
        featuredLeaderboard.config,
        this.isLeaderboardActive(leaderboardData.endDate, date),
        undefined,
      );
    }

    return undefined;
  }

  public async getLeaderboardConfigsForOrganisation(organisation: string): Promise<ILeaderboardConfig[]> {
    const leaderboardsConfigs: ILeaderboardConfig[] = await this.leaderboardConfigRepository
      .getLeaderboardConfigsForOrganisation(organisation);

    return leaderboardsConfigs.sort((a, b) => a.order - b.order);
  }

  public async getLeaderboardInstancesForWorkout(workout: UserWorkoutSession, date: Date)
    : Promise<ILeaderboardInstance[]> {
    const user = await this.userProfileRepository.get(workout.userProfileId);
    if (!user) {
      throw NotFoundError.User(workout.userProfileId);
    }

    const leaderboardsConfigsForWorkout: ILeaderboardConfig[] = await this.getActiveLeaderboardConfigsForWorkout(
      user,
      date,
    );

    const filteredLeaderboardConfigs = leaderboardsConfigsForWorkout.filter((leaderboardConfig) => {
      if (workout.importOrigin && !leaderboardConfig.includeImportedWorkouts) {
        return false;
      }
      return true;
    });

    const leaderboardsAssociatedWithUser = filteredLeaderboardConfigs.filter((leaderboardConfig) =>
      !this.calculateIfUserHasToOptedIn(leaderboardConfig, user, true));

    const promises = filteredLeaderboardConfigs.map(async (leaderboardConfig, index) => {
      const leaderboardInstance = getLeaderboardInstance(leaderboardConfig, date);

      leaderboardInstance.calculatedScore = this.capScoresBasedOnMaxTime(
        leaderboardConfig,
        leaderboardInstance,
        workout,
      );

      const teamName = this.getTeamUserIsOptedInto(
        user,
        leaderboardInstance.leaderboardId,
        new Date(leaderboardInstance.endDate),
      );
      const allUsersAssociatedWithLeaderboard = await this
        .userProfileRepository
        .getLeaderboardPreferencesForUsersByLeaderboardId(leaderboardConfig._id);

      if (leaderboardConfig.competitionType === 'inter-team') {
        if (!teamName) {
          return null;
        }
        leaderboardInstance.team = teamName;
        if (leaderboardConfig.metric.includes('weighted')) {
          const usersInTeamWithNoOptOutTime = getCurrentMembersInTeam(allUsersAssociatedWithLeaderboard, teamName);
          leaderboardInstance.teamSize = usersInTeamWithNoOptOutTime;
          leaderboardInstance.calculatedScore = workout.calculateWeightedWorkoutScore(
            usersInTeamWithNoOptOutTime,
            leaderboardConfig.metric,
            leaderboardInstance.calculatedScore,
          );
        }
      }

      if (leaderboardsAssociatedWithUser.includes(leaderboardConfig)) {
        return leaderboardInstance;
      }

      return null;
    });

    const rawInstances = await Promise.all(promises);
    const leaderboardConfigInstances = rawInstances.filter(
      (instance): instance is ILeaderboardInstance => instance !== null,
    );

    return leaderboardConfigInstances;
  }

  public async getLeaderboardBreakdownForUser(
    uid: string,
    leaderboardConfigId: string,
    startDate: Date,
    endDate: Date,
    team?: string,
  ): Promise<IUserWeightedLeaderboardBreakdown[]> {
    const user = await this.userProfileRepository.getUserProfileOrThrow(uid);
    const includeHomeWorkouts = true;

    const allUserWorkouts = await this.workoutRepository.getAllWorkoutsForUsers(
      [user.uid],
      startDate,
      endDate,
      includeHomeWorkouts,
    );
    const leaderboardSpecificWorkouts = allUserWorkouts.filter((workout) =>
      workout.leaderboards
      && workout.leaderboards.some((leaderboard) => leaderboard.leaderboardId === leaderboardConfigId)
      && new Date(workout.startTime) >= startDate
      && new Date(workout.endTime) <= endDate);

    const userScores: IUserWeightedLeaderboardBreakdown[] = [];

    leaderboardSpecificWorkouts.forEach((workout) => {
      const totalMins = UserWorkoutSession.calculateWorkoutDurationInMinutes(
        workout.startTimeOfFirstExercise,
        workout.endTime,
        workout.additionalSecondsBeforeFirstExercise,
      );

      workout.leaderboards?.forEach((leaderboard) => {
        const isCorrectLeaderboardInfo = team ? leaderboard.team === team : true;
        if (leaderboard.leaderboardId === leaderboardConfigId && leaderboard.calculatedScore !== undefined) {
          const scoreBreakdown: IUserWeightedLeaderboardBreakdown = {
            calculatedScore: leaderboard.calculatedScore,
            teamSize: leaderboard.teamSize ?? 1,
            totalScore: leaderboard.metric.includes('minutes')
              ? totalMins
              : workout.performanceMetrics?.load ?? 0,
            dateCompleted: workout.endTime,
          };
          if (isCorrectLeaderboardInfo) {
            userScores.push(scoreBreakdown);
          }
        }
      });
    });

    return userScores;
  }

  public async createLeaderboardConfig(leaderboardConfig: ILeaderboardConfig): Promise<void> {
    await this.updateLeaderboardOrders(leaderboardConfig);
    await this.leaderboardConfigRepository.add(leaderboardConfig);
  }

  public createLeaderboardIdConfigId(orgName: string, startDate: Date, gymName?: string): string {
    return (`
      ${orgName}
      ${gymName
        ? `-${gymName}`
        : ''}
      -${moment(startDate).year().toString()}
      -${moment(startDate).format('MMMM')}
      -${Date.now().toString()}`
    ).replace(/\n\s*/g, '');
  }

  public async updateLeaderboardConfig(leaderboardConfig: ILeaderboardConfig): Promise<void> {
    await this.leaderboardConfigRepository.update(leaderboardConfig);
  }

  public async arrangeLeaderboards(
    leaderboardConfigs: ILeaderboardConfig[],
  ): Promise<ILeaderboardConfig[]> {
    const updateLeaderboardConfigs: ILeaderboardConfig[] = [];
    for (let i = 0; i < leaderboardConfigs.length; i++) {
      const singleLeaderboardConfig = leaderboardConfigs[i];
      const leaderboardConfigToUpdate = await this.leaderboardConfigRepository.get(singleLeaderboardConfig._id);
      leaderboardConfigToUpdate.order = i + 1;
      await this.leaderboardConfigRepository.update(leaderboardConfigToUpdate);
      updateLeaderboardConfigs.push(leaderboardConfigToUpdate);
    }
    return updateLeaderboardConfigs;
  }

  public async getLeaderboardDataForLeaderboardInstance(
    leaderboardId: string,
    startDate: Date,
    endDate: Date,
    team?: string,
    userId?: string,
  ): Promise<IUserLeaderboardViewModel | ILeaderboardDataViewModel> {
    const showIndividualDataWhenTeamIsProvided = !!team;
    const leaderboardConfig = await this.leaderboardConfigRepository.get(leaderboardId);
    const leaderboardInstance: ILeaderboardInstance = {
      leaderboardId,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      metric: leaderboardConfig.metric,
      team,
    };

    const showIndividualDataBecauseTeamIsProvided = !!team;
    const shouldShowIndividualData = leaderboardConfig.competitionType === 'individual'
      || showIndividualDataBecauseTeamIsProvided;
    const rankedLeaderboardData = await this.getRankedLeaderboardData(
      leaderboardConfig,
      leaderboardInstance,
      team,
    );

    if (showIndividualDataWhenTeamIsProvided) {
      const usersWithNoRankings = await this.createRankingsForAllUsersInTeamWithNoRankings(
        team,
        leaderboardConfig,
        rankedLeaderboardData,
        userId,
      );

      rankedLeaderboardData.rankings = rankedLeaderboardData.rankings.concat(usersWithNoRankings);
    }

    if (userId) {
      const user = await this.userProfileRepository.get(userId);

      if (!user) {
        throw NotFoundError.User(userId);
      }

      const mappedLeaderboardDataForUsers = await this.mapLeaderboardDataForUser(
        user,
        rankedLeaderboardData,
        {
          ...leaderboardConfig,
          ...showIndividualDataWhenTeamIsProvided ? { competitionType: 'individual' } : {},
        },
        this.isLeaderboardActive(rankedLeaderboardData.endDate, new Date()),
        team,
      );

      return mappedLeaderboardDataForUsers;
    }

    if (!shouldShowIndividualData) {
      if (!leaderboardConfig.teams) {
        throw new Error('invalid leaderboard config: teams are required when competition is inter team');
      }

      rankedLeaderboardData.rankings = await this.getFlattenedTeamData(
        rankedLeaderboardData,
        leaderboardConfig.teams,
        /* calculateUserScoreCallback */ undefined,
        leaderboardConfig.countZeroScores,
      );
      const teamLeaderboardRankings: ILeaderboardRankingViewModel[] = await this.mapTeamsToRankings(
        rankedLeaderboardData.rankings,
        team,
      );
      const mappedLeaderboardDataForTeams: ILeaderboardDataViewModel = {
        ...rankedLeaderboardData,
        teams: leaderboardConfig.teams,
        rankings: teamLeaderboardRankings,
      };
      return mappedLeaderboardDataForTeams;
    }

    const individualLeaderboardRankings: ILeaderboardRankingViewModel[] = await this.mapUsersToRankings(
      rankedLeaderboardData.rankings,
      undefined,
      rankedLeaderboardData,
    );
    const mappedIndividualLeaderboardRankings: ILeaderboardDataViewModel = {
      ...rankedLeaderboardData,
      teams: leaderboardConfig.teams,
      rankings: individualLeaderboardRankings,
    };
    return mappedIndividualLeaderboardRankings;
  }

  public async optInToLeaderboard(uid: string, leaderboardId: string, requestTime: Date, team?: string) {
    const user = await this.userProfileRepository.getUserProfileOrThrow(uid);
    const leaderboardPreference = this.getOrCreateLeaderboardPreferencesForLeaderboard(
      user,
      leaderboardId,
    );

    leaderboardPreference.optInHistory
      .push(this.createOptInHistoryEntry(requestTime, undefined, team));
    await this.userProfileRepository.update(user);
  }

  public async optOutOfLeaderboard(uid: string, leaderboardId: string, requestTime: Date) {
    const user = await this.userProfileRepository.getUserProfileOrThrow(uid);

    const leaderboardPreference = this.getOrCreateLeaderboardPreferencesForLeaderboard(
      user,
      leaderboardId,
    );

    const history: ILeaderboardOptInHistory[] = leaderboardPreference.optInHistory;
    const hasOptInHistoryEntries = history.length > 0;

    if (hasOptInHistoryEntries) {
      const latestEntry = history[history.length - 1];
      latestEntry.optOutTime = requestTime;
    } else {
      leaderboardPreference.optInHistory
        .push(this.createOptInHistoryEntry(undefined, requestTime));
    }

    await this.userProfileRepository.update(user);
  }

  public async changeOptInTeam(uid: string, leaderboardId: string, requestTime: Date, team: string) {
    await this.optOutOfLeaderboard(uid, leaderboardId, requestTime);
    await this.optInToLeaderboard(uid, leaderboardId, requestTime, team);
  }

  public async getDetailedIndividualLeaderboardData(
    leaderboardId: string,
    dateForInstance: Date,
  ): Promise<ILeaderboardData> {
    const leaderboardConfig = await this.leaderboardConfigRepository.get(leaderboardId);

    const leaderboardInstanceWithFullContext = getLeaderboardInstance(leaderboardConfig, dateForInstance);
    const rawDataFromRepo: LeaderboardData = await this.workoutRepository.getLeaderboardData(
      leaderboardInstanceWithFullContext,
      true, // shouldShowIndividualData to get per-user entries
      undefined, // No specific team filter
    );

    const mappedData = await this.mapLeaderboardData(
      rawDataFromRepo,
      leaderboardInstanceWithFullContext,
      leaderboardConfig,
    );
    return mappedData;
  }

  private capScoresBasedOnMaxTime(
    leaderboardConfig: ILeaderboardConfig,
    leaderboardInstance: ILeaderboardInstance,
    workout: UserWorkoutSession,
  ): number | undefined {
    const totalMinutes = workout.calculateWorkoutDurationInMinutes();
    const cappedTime = leaderboardConfig.capMinutes;

    if (cappedTime && totalMinutes > cappedTime) {
      if (this.isLeaderboardBasedOn(leaderboardConfig, 'minutes')) {
        return cappedTime;
      }
      if (this.isLeaderboardBasedOn(leaderboardConfig, 'points') && workout.performanceMetrics) {
        const ratio = cappedTime / totalMinutes;
        const adjustedPointsBasedOnTimeCap = Math.round(workout.performanceMetrics.load * ratio);
        return adjustedPointsBasedOnTimeCap;
      }
    }
    return leaderboardInstance.calculatedScore;
  }

  private isLeaderboardBasedOn(leaderboradConfig: ILeaderboardConfig, metric: string): boolean {
    return leaderboradConfig.metric.includes(metric);
  }

  private async updateLeaderboardOrders(leaderboardConfig: ILeaderboardConfig): Promise<void> {
    const leaderboardsConfigsForGym = await this.leaderboardConfigRepository.getLeaderboardConfigsForOrganisation(
      leaderboardConfig.organisation,
    );

    const insertOrder: number = leaderboardConfig.order;

    const updateOrders = async (config: ILeaderboardConfig) => {
      if (config.order >= insertOrder) {
        config.order += 1;
        await this.leaderboardConfigRepository.update(config);
      }
    };

    await Promise.all(leaderboardsConfigsForGym.map(updateOrders));
  }

  private getOrCreateLeaderboardPreferencesForLeaderboard(user: UserProfile, leaderboardId: string) {
    if (!user.leaderboardPreferences) {
      user.leaderboardPreferences = {
        leaderboardPreferences: {
          [leaderboardId]: {
            optInHistory: [],
          },
        },
      };
    }

    if (!user.leaderboardPreferences.leaderboardPreferences[leaderboardId]) {
      user.leaderboardPreferences.leaderboardPreferences[leaderboardId] = {
        optInHistory: [],
      };
    }

    return user.leaderboardPreferences.leaderboardPreferences[leaderboardId];
  }

  private createOptInHistoryEntry(
    optInTime?: Date,
    optOutTime?: Date,
    team?: string,
  ): ILeaderboardOptInHistory {
    return {
      optInTime,
      optOutTime,
      team,
    };
  }

  private getLeaderboardRankingsView(
    rankings: ILeaderboardRanking[],
    user: UserProfile,
    team: string | undefined,
    leaderboardData: ILeaderboardData,
  ) {
    if (team) {
      const teamUserIsOptedInto = this.getTeamUserIsOptedInto(
        user,
        leaderboardData.leaderboardId,
        leaderboardData.endDate,
      );
      if (!!teamUserIsOptedInto && teamUserIsOptedInto === team) {
        return rankings;
      }

      return rankings.slice(0, this.leaderboardAmountOfOtherTeamMembersToShow);
    }

    if (leaderboardData.competitionType === 'individual') {
      return this.limitAmountOfRankings(user, rankings);
    }

    return rankings;
  }

  private limitAmountOfRankings(
    user: UserProfile,
    rankings: ILeaderboardRanking[],
  ): ILeaderboardRanking[] {
    const mappedRankings: ILeaderboardRankingDisplayModel[] = rankings
      .map((ranking: ILeaderboardRanking, index: number): ILeaderboardRankingDisplayModel => ({
        ...ranking,
        isMine: ranking.userProfileId === user.uid,
        showEllipseBottom: false,
      }));

    const indexOfUserRanking = mappedRankings.findIndex((ranking: ILeaderboardRankingDisplayModel) => ranking.isMine);
    const position = indexOfUserRanking !== -1 ? indexOfUserRanking + 1 : undefined;

    const upperLimit = Math.max(
      rankings.length * this.percentageLimitAmountOfIndividualUsersToShow,
      this.lowerLimitAmountOfIndividualUsersToShow,
    );
    const placeholderForRankingsInFrontAndBehindUser = 3;
    const topRows = position !== undefined
      ? (position < upperLimit ? upperLimit : upperLimit - placeholderForRankingsInFrontAndBehindUser)
      : upperLimit;
    let rows: ILeaderboardRankingDisplayModel[] = mappedRankings.slice(0, topRows);
    const userRanking: ILeaderboardRankingDisplayModel | undefined = mappedRankings
      .find((ranking) => ranking.isMine);
    const isUserInTopRows = !!position && position <= topRows;

    const isUserInTheTopRankings = !isUserInTopRows && !!userRanking && !!position;
    if (isUserInTheTopRankings) {
      rows = rows.concat(this.getRankingsInFrontAndBehindUser(mappedRankings, position, userRanking));
    }

    return rows;
  }

  private getRankingsInFrontAndBehindUser(
    rankings: ILeaderboardRankingDisplayModel[],
    position: number,
    userRanking: ILeaderboardRankingDisplayModel,
  ): ILeaderboardRankingDisplayModel[] {
    const rankingsInFrontAndBehindUser: ILeaderboardRankingDisplayModel[] = [];

    const indexOfUserRankingBeforeCurrentPosition = position - 2;
    const userRankinBeforeCurrentPosition = rankings[indexOfUserRankingBeforeCurrentPosition];
    if (userRankinBeforeCurrentPosition) {
      rankingsInFrontAndBehindUser.push(userRankinBeforeCurrentPosition);
    }

    rankingsInFrontAndBehindUser.push(userRanking);

    const indexOfUserRankingAfterCurrentPosition = position;
    const userRankinAfterCurrentPosition = rankings[indexOfUserRankingAfterCurrentPosition];
    if (userRankinAfterCurrentPosition) {
      rankingsInFrontAndBehindUser.push(userRankinAfterCurrentPosition);
    }

    return rankingsInFrontAndBehindUser;
  }

  private async createRankingsForAllUsersInTeamWithNoRankings(
    team: string,
    leaderboardConfig: ILeaderboardConfig,
    mappedLeaderboardData: ILeaderboardData,
    userId?: string,
  ): Promise<ILeaderboardRanking[]> {
    if (!leaderboardConfig.teams) {
      throw new Error('invalid leaderboard config: teams are required when competition is inter team');
    }

    const uniqueUsers: string[] = mappedLeaderboardData.rankings.map((ranking: ILeaderboardRanking) => {
      if (!ranking.userProfileId) {
        throw new Error('ranking needs to have a userprofileId when individual level');
      }
      return ranking.userProfileId;
    });

    const uniqueValues = new Set(uniqueUsers);
    const usersWithLeaderboardPreferences = await this.userProfileRepository
      .getLeaderboardPreferencesForUsersByLeaderboardId(leaderboardConfig._id);

    const uniqueUsersWhoHaveNoRankings = this.getUsersWithNoRankings(
      usersWithLeaderboardPreferences,
      uniqueValues,
      mappedLeaderboardData,
      team,
    );

    let rank = mappedLeaderboardData.rankings.length + 1;

    const lastRanking = mappedLeaderboardData.rankings[mappedLeaderboardData.rankings.length - 1];
    rank = this.calculateRankForScore(0, lastRanking);

    const newRankingsForUsersWhoHaveNoRankings: ILeaderboardRanking[] = uniqueUsersWhoHaveNoRankings
      .map((user: IUserLeaderboardPreferenceViewResult): ILeaderboardRanking => ({
        userProfileId: user.userProfileId,
        rank,
        value: 0,
        teamName: team,
      }));

    return newRankingsForUsersWhoHaveNoRankings;
  }

  private getUsersWithNoRankings(
    usersWithLeaderboardPreferences: IUserLeaderboardPreferenceViewResult[],
    uniqueValues: Set<string>,
    mappedLeaderboardData: ILeaderboardData,
    team: string,
  ) {
    const uniqueUsersWhoHaveNoRankings: IUserLeaderboardPreferenceViewResult[] = usersWithLeaderboardPreferences
      .filter((userLeaderboardPreference: IUserLeaderboardPreferenceViewResult) => {
        const userAlreadyExists = uniqueValues.has(userLeaderboardPreference.userProfileId);
        const latestTeam = this.getLatestOptedIntoTeam(
          userLeaderboardPreference.leaderboardPreference,
          mappedLeaderboardData.endDate,
        );

        return !userAlreadyExists && latestTeam && latestTeam === team;
      });
    return uniqueUsersWhoHaveNoRankings;
  }

  private async mapLeaderboardData(
    leaderboardData: LeaderboardData,
    leaderboardInstance: ILeaderboardInstance,
    leaderboardConfig: ILeaderboardConfig,
  ): Promise<ILeaderboardData> {
    const isLeaderboardIndividual = this.isIndividualData(leaderboardData);

    const rankings = this.calculateRanksForLeaderboard(
      leaderboardData,
      isLeaderboardIndividual,
    );

    return {
      leaderboardId: leaderboardInstance.leaderboardId,
      name: leaderboardConfig.name,
      metric: leaderboardInstance.metric,
      competitionType: leaderboardConfig.competitionType,
      startDate: new Date(leaderboardInstance.startDate),
      endDate: new Date(leaderboardInstance.endDate),
      frequency: leaderboardConfig.frequency,
      timezone: leaderboardConfig.timezone,
      rankings,
    };
  }

  private calculateRanksForLeaderboard(
    leaderboardData: LeaderboardData,
    isLeaderboardIndividual: boolean,
  ): ILeaderboardRanking[] {
    const rankings: ILeaderboardRanking[] = [];
    leaderboardData.forEach((leaderboardItem, index) => {
      const personInFront = rankings[index - 1];
      this.calculateRankForScore(leaderboardItem.value, personInFront);

      rankings.push({
        value: leaderboardItem.value,
        rank: this.calculateRankForScore(leaderboardItem.value, personInFront),
        teamName: !isLeaderboardIndividual ? (leaderboardItem as IInnerTeamLeaderboardData).team : undefined,
        userProfileId: leaderboardItem.userProfileId,
      });
    });

    return rankings;
  }

  private calculateRankForScore(score: number, rankingInFrontOfThisScore?: ILeaderboardRanking) {
    if (!rankingInFrontOfThisScore) {
      return 1;
    }
    if (rankingInFrontOfThisScore.value === score) {
      return rankingInFrontOfThisScore.rank;
    }
    return rankingInFrontOfThisScore.rank + 1;
  }

  private getUserDisplayName(userProfile: Partial<UserProfile>): string {
    return userProfile.nickname || userProfile.firstname || userProfile.lastname || '';
  }

  private async getRankedLeaderboardData(
    leaderboardConfig: ILeaderboardConfig,
    leaderboardIdentifier: ILeaderboardInstance,
    filterLeaderboardDataForTeam?: string,
  ): Promise<ILeaderboardData> {
    const shouldShowIndividualData = leaderboardConfig.competitionType === 'individual'
      || !!filterLeaderboardDataForTeam;
    const leaderboardData: LeaderboardData = await this.workoutRepository.getLeaderboardData(
      leaderboardIdentifier,
      shouldShowIndividualData,
      filterLeaderboardDataForTeam,
    );

    return this.mapLeaderboardData(
      leaderboardData,
      leaderboardIdentifier,
      leaderboardConfig,
    );
  }

  private isIndividualData(data: LeaderboardData):
    data is IIndividualLeaderboardData[] {
    return data.every((item) => typeof item === 'object' && !('team' in item));
  }

  private async mapLeaderboardDataForUser(
    user: UserProfile,
    leaderboardData: ILeaderboardData,
    leaderboardConfig: ILeaderboardConfig,
    isLeaderboardActive: boolean,
    team: string | undefined,
  ): Promise<IUserLeaderboardViewModel> {
    if (leaderboardConfig.competitionType === 'inter-team') {
      if (!leaderboardConfig.teams) {
        throw new Error('invalid leaderboard config: teams are required when competition is inner team');
      }

      return this.getUserAggregatedTeamData(
        user,
        leaderboardData,
        leaderboardConfig,
        isLeaderboardActive,
        leaderboardConfig.teams,
      );
    }

    const getUserPosition = () => {
      const position = leaderboardData.rankings
        .findIndex((ranking: ILeaderboardRanking) => ranking.userProfileId === user.uid);
      return position !== -1 ? position + 1 : undefined;
    };

    const userRanking = getUserPosition();

    const totalParticipants = leaderboardData.rankings.length;
    leaderboardData.rankings = this.getLeaderboardRankingsView(
      leaderboardData.rankings,
      user,
      team,
      leaderboardData,
    );

    const userRankings: ILeaderboardRankingViewModel[] = await this.mapUsersToRankings(
      leaderboardData.rankings,
      user,
    );

    return {
      ...leaderboardData,
      teams: leaderboardConfig.teams,
      requireOptIn: this.calculateIfUserHasToOptedIn(leaderboardConfig, user, isLeaderboardActive),
      rankings: userRankings,
      position: userRanking,
      totalParticipants,
    };
  }

  private async mapUsersToRankings(
    rankings: ILeaderboardRanking[],
    user?: UserProfile,
    leaderboardConfigDetails?: ILeaderboardData,
  ): Promise<ILeaderboardRankingViewModel[]> {
    const userProfileMap = {};

    const userIds = rankings.map((data) => data.userProfileId!);
    const allUsers = await this.userProfileRepository.getPotentiallyDeletedUsersByIds(userIds);
    allUsers.forEach((userRow: UserProfile) => {
      userProfileMap[userRow.uid] = userRow;
    });

    const getIndividualName = (userProfileId: string): string => {
      const userProfile = userProfileMap[userProfileId];
      if (!userProfile) {
        throw new NotFoundError('User', userProfileId);
      }

      return this.getUserDisplayName(userProfile);
    };

    let mappedRankings: ILeaderboardRankingViewModel[] = rankings.map((ranking: ILeaderboardRanking) => {
      const teamUserIsOptedInto = leaderboardConfigDetails
        ? this.getTeamUserIsOptedInto(
          userProfileMap[ranking.userProfileId!],
          leaderboardConfigDetails?.leaderboardId,
          leaderboardConfigDetails?.endDate,
        )
        : ranking.teamName;
      const shouldIncludePersonalDetailsForBusinessTools = !!leaderboardConfigDetails;
      const userProfileDetails = userProfileMap[ranking.userProfileId!];
      return {
        name: getIndividualName(ranking.userProfileId!),
        rank: ranking.rank,
        value: ranking.value,
        isMine: user && ranking.userProfileId === user.uid,
        teamName: ranking.teamName,
        currentTeam: teamUserIsOptedInto,
        userProfileId: ranking.userProfileId,
        ...shouldIncludePersonalDetailsForBusinessTools ? {
          email: userProfileDetails.email,
          lastname: userProfileDetails.lastname,
        } : {},
      };
    });

    mappedRankings = this.alphabeticallyOrderAllRanksWithValueZero(mappedRankings);
    return mappedRankings;
  }

  private alphabeticallyOrderAllRanksWithValueZero(
    rankings: ILeaderboardRankingViewModel[],
  ): ILeaderboardRankingViewModel[] {
    const allRankingThatHaveValueNotEqualZero = rankings
      .filter((item) => item.value !== 0);
    const allRankingThatHaveValueEqualZero = rankings
      .filter((item) => item.value === 0);

    allRankingThatHaveValueEqualZero
      .sort((a, b) => a.name.localeCompare(b.name));

    const rankingsWithAlphabeticallySortedRankingsWithZeroScore = allRankingThatHaveValueNotEqualZero
      .concat(allRankingThatHaveValueEqualZero);

    return rankingsWithAlphabeticallySortedRankingsWithZeroScore;
  }

  private async mapTeamsToRankings(
    rankings: ILeaderboardRanking[],
    team?: string,
  ): Promise<ILeaderboardRankingViewModel[]> {
    const mappedRankings: ILeaderboardRankingViewModel[] = rankings.map((ranking: ILeaderboardRanking) => {
      if (!ranking.teamName) {
        throw new Error('teamName is required to calculate team rankings');
      }

      return {
        name: ranking.teamName,
        rank: ranking.rank,
        value: ranking.value,
        isMine: !!ranking.teamName && ranking.teamName === team,
        teamName: ranking.teamName || team,
      };
    });
    return mappedRankings;
  }

  private orderUserLeaderboardInstances(
    leaderboardsForUser: IUserLeaderboardInstanceWrapper[],
  ): IUserLeaderboardInstanceWrapper[] {
    const sortByOrder = (
      a: { config: { order: number } },
      b: { config: { order: number } },
    ) => a.config.order - b.config.order;

    const sortByStartDate = (
      a: { endDate: Date },
      b: { endDate: Date },
    ) => b.endDate.getTime() - a.endDate.getTime();

    const activeLeaderboards = leaderboardsForUser.filter(
      (leaderboard) => leaderboard.isActive,
    );

    const pastLeaderboards = leaderboardsForUser.filter(
      (leaderboard) => !leaderboard.isActive,
    );

    const activeLeaderboardsOrderedByRank = activeLeaderboards.sort(sortByOrder);
    let orderedPastLeaderboards = pastLeaderboards.sort(sortByStartDate).sort(sortByOrder);

    const firstPastLeaderboard: IUserLeaderboardInstanceWrapper = orderedPastLeaderboards[0];

    if (firstPastLeaderboard) {
      const firstPastLeaderboardRemoved = orderedPastLeaderboards.splice(0, 1)[0];
      orderedPastLeaderboards = orderedPastLeaderboards.sort(sortByStartDate);
      orderedPastLeaderboards = [firstPastLeaderboardRemoved, ...orderedPastLeaderboards];
    }

    return [...activeLeaderboardsOrderedByRank, ...orderedPastLeaderboards];
  }

  private async getActiveAndUpcomingLeaderboardConfigs(gymName: string, date: Date) {
    const gym = DbGymRepository.getByName(gymName);
    const organisation = EnabledFeaturesHydrationService.getGymOrganisation(gym);

    const leaderboardsConfigsForUser: ILeaderboardConfig[] = await this.leaderboardConfigRepository
      .getApplicableLeaderboardConfigs(date, organisation.name, gym.name, 'active-or-future');

    return leaderboardsConfigsForUser;
  }

  private async getAllLeaderboardConfigs(gymName: string, date: Date) {
    const gym = DbGymRepository.getByName(gymName);
    const organisation = EnabledFeaturesHydrationService.getGymOrganisation(gym);

    const leaderboardsConfigsForUser: ILeaderboardConfig[] = await this.leaderboardConfigRepository
      .getApplicableLeaderboardConfigs(date, organisation.name, gym.name, 'all');

    return leaderboardsConfigsForUser;
  }

  private async getFlattenedTeamData(
    leaderboardData: ILeaderboardData,
    teams: string[],
    calculateUserScoreCallback?: (score: number, team: string, userProfileId: string) => void,
    countZeroScores: boolean = false,
  ): Promise<ILeaderboardRanking[]> {
    const today = new Date();
    const usersWithLeaderboardPreferences = await this.userProfileRepository
      .getLeaderboardPreferencesForUsersByLeaderboardId(leaderboardData.leaderboardId);
    const totalDaysIntoChallenge = getDaysIntoChallenge(
      today,
      leaderboardData.startDate,
      leaderboardData.endDate,
      leaderboardData.timezone,
    );

    const rankingPlaceholder = 0;
    const totalMembersPerTeam: Record<string, number> = getCountTotalMembersPerTeam(
      usersWithLeaderboardPreferences,
      countZeroScores,
    );

    const usersWithActiveLeaderboardPreference = getUsersWithActiveLeaderboardPreference(
      usersWithLeaderboardPreferences,
      leaderboardData,
    );

    const flattenedRankings: ILeaderboardRanking[] = leaderboardData.rankings
      .reduce((ranking: ILeaderboardRanking[], currentValue: ILeaderboardRanking) => {
        const existingTeam = ranking
          .find((team) => team.teamName === currentValue.teamName);

        if (currentValue.userProfileId && calculateUserScoreCallback && currentValue.teamName) {
          calculateUserScoreCallback(currentValue.value, currentValue.teamName, currentValue.userProfileId);
        }

        if (!currentValue.teamName) {
          throw new Error('team is required');
        }

        let userScore = currentValue.value;

        if (leaderboardData.metric === 'average-minutes') {
          userScore = this.calculateUserAverageMinutes(
            today,
            userScore,
            currentValue,
            usersWithActiveLeaderboardPreference,
            totalDaysIntoChallenge,
            leaderboardData,
          );
        }
        if (existingTeam) {
          existingTeam.value += userScore;
        } else {
          ranking.push({ teamName: currentValue.teamName, rank: rankingPlaceholder, value: userScore });
        }

        if (!countZeroScores) {
          totalMembersPerTeam[currentValue.teamName]
            ? totalMembersPerTeam[currentValue.teamName] += 1
            : totalMembersPerTeam[currentValue.teamName] = 1;
        }

        return ranking;
      }, []);

    if (leaderboardData.metric === 'average-minutes') {
      flattenedRankings.forEach((ranking: ILeaderboardRanking) => {
        if (!ranking.teamName) {
          throw new Error('teamName is required');
        }

        const totalInTeam = totalMembersPerTeam[ranking.teamName] || 1;
        ranking.value /= totalInTeam;
      });
    }

    teams.forEach((team: string) => {
      if (!flattenedRankings.find((ranking: ILeaderboardRanking) => ranking.teamName === team)) {
        flattenedRankings.push({
          teamName: team,
          value: 0,
          rank: rankingPlaceholder,
        });
      }
    });

    const sortedRankings = flattenedRankings.sort((a, b) => b.value - a.value);
    this.calculateRanksForFlattenedTeam(sortedRankings);

    return sortedRankings;
  }

  private calculateUserAverageMinutes(
    today: Date,
    userTotalScoreForTeam: number,
    currentRanking: ILeaderboardRanking,
    usersWithLeaderboardPreferences: IUserLeaderboardPreferenceViewResult[],
    totalDaysIntoChallenge: number,
    leaderboardData: ILeaderboardData,
  ): number {
    if (!currentRanking.teamName) {
      throw new Error('team is required');
    }

    if (!currentRanking.userProfileId) {
      throw new Error('userProfileId is required');
    }

    const preferences = usersWithLeaderboardPreferences
      .find((preference) => preference.userProfileId === currentRanking.userProfileId);
    if (!preferences) {
      return userTotalScoreForTeam / totalDaysIntoChallenge;
    }
    const { optInHistory } = preferences.leaderboardPreference;

    const daysOptedIntoTeamLeaderboard = getDaysOptedIntoTeamLeaderboard(
      today,
      leaderboardData.startDate,
      leaderboardData.endDate,
      leaderboardData.timezone,
      optInHistory,
      currentRanking.teamName,
    );
    return (userTotalScoreForTeam / (daysOptedIntoTeamLeaderboard || totalDaysIntoChallenge));
  }

  private calculateRanksForFlattenedTeam(
    rankings: ILeaderboardRanking[],
  ) {
    rankings.forEach((ranking: ILeaderboardRanking, index: number) => {
      let rank = index + 1;
      const personInFront = rankings[index - 1];
      rank = this.calculateRankForScore(ranking.value, personInFront);
      ranking.rank = rank;
    });
  }

  private async getUserAggregatedTeamData(
    user: UserProfile,
    leaderboardData: ILeaderboardData,
    leaderboardConfig: ILeaderboardConfig,
    isLeaderboardActive: boolean,
    teams: string[],
  ): Promise<IUserLeaderboardViewModel> {
    const aggregatedData: { value: number, team?: string } = {
      value: 0,
      team: undefined,
    };

    function aggregateDataCallback(value: number, team: string, userProfileId: string) {
      if (userProfileId === user.uid) {
        aggregatedData.value = (aggregatedData.value ?? 0) + value;
        aggregatedData.team = team;
      }
    }

    const flattenedTeamRankings = await this.getFlattenedTeamData(
      leaderboardData,
      teams,
      aggregateDataCallback,
      leaderboardConfig.countZeroScores,
    );

    if (leaderboardConfig.requireOptIn) {
      aggregatedData.team = this.getTeamUserIsOptedInto(user, leaderboardData.leaderboardId, leaderboardData.endDate);
    }

    const userRanking: number = flattenedTeamRankings
      .findIndex((ranking: ILeaderboardRanking) => ranking.teamName === aggregatedData.team);

    const position = userRanking !== -1 ? userRanking + 1 : undefined;

    const mappedTeamRankings: ILeaderboardRankingViewModel[] = flattenedTeamRankings
      .map((ranking: ILeaderboardRanking) => {
        if (!ranking.teamName) {
          throw new Error('ranking is required');
        }

        return {
          isMine: ranking.teamName === aggregatedData.team,
          name: ranking.teamName,
          rank: ranking.rank,
          value: ranking.value,
        };
      });

    return {
      ...leaderboardData,
      teams: leaderboardConfig.teams,
      requireOptIn: this.calculateIfUserHasToOptedIn(leaderboardConfig, user, isLeaderboardActive),
      rankings: mappedTeamRankings,
      position,
      contribution: aggregatedData.value,
      totalParticipants: flattenedTeamRankings.length,
    };
  }

  private isLeaderboardActive(endDate: Date, today: Date): boolean {
    return endDate > today;
  }

  private calculateIfUserHasToOptedIn(
    leaderboardConfig: ILeaderboardConfig,
    user: UserProfile,
    isActive: boolean,
  ): boolean {
    const leaderboardPreference = user.leaderboardPreferences?.leaderboardPreferences[leaderboardConfig._id];
    if (isActive && leaderboardPreference) {
      const mostRecentOptInHistory = leaderboardPreference.optInHistory[leaderboardPreference.optInHistory.length - 1];

      if (mostRecentOptInHistory) {
        return !!mostRecentOptInHistory.optOutTime;
      }
    }

    return leaderboardConfig.requireOptIn && isActive;
  }

  private getTeamUserIsOptedInto(user: UserProfile, leaderboardId: string, endDate: Date): string | undefined {
    const leaderboardPreference = user.leaderboardPreferences?.leaderboardPreferences[leaderboardId];

    if (!leaderboardPreference) {
      return undefined;
    }
    return this.getLatestOptedIntoTeam(leaderboardPreference, endDate);
  }

  private getLatestOptedIntoTeam(leaderboardPreference: IUserLeaderboardPreference, endDate: Date): string | undefined {
    const filteredOptInHistory = leaderboardPreference.optInHistory.filter(
      (optInHistoryEntry: ILeaderboardOptInHistory) => {
        const latestDate = optInHistoryEntry.optOutTime || optInHistoryEntry.optInTime;
        return !(latestDate && latestDate > endDate);
      },
    );

    const lastLeaderboardOptInHistoryEntry = filteredOptInHistory[filteredOptInHistory.length - 1];

    if (!lastLeaderboardOptInHistoryEntry) {
      return undefined;
    }

    const leaderboardIsOptedIn = !!lastLeaderboardOptInHistoryEntry.optInTime
      && !lastLeaderboardOptInHistoryEntry.optOutTime;

    if (leaderboardIsOptedIn) {
      return lastLeaderboardOptInHistoryEntry.team;
    }

    return undefined;
  }

  private async getActiveLeaderboardConfigsForWorkout(user: UserProfile, date: Date) {
    if (!user.gymName) {
      throw new NotFoundError('gymName', user.uid);
    }

    const leaderboardsConfigsForUser: ILeaderboardConfig[] = await this.getActiveAndUpcomingLeaderboardConfigs(
      user.gymName,
      date,
    );

    const filteredOutFutureLeaderboards: ILeaderboardConfig[] = leaderboardsConfigsForUser.filter(
      (leaderboardConfig: ILeaderboardConfig) => leaderboardConfig.startDate < date,
    );

    return filteredOutFutureLeaderboards;
  }

  private isLeaderboardRelevantForUser(
    leaderboard: IUserLeaderboardInstanceWrapper,
    user: UserProfile,
  ): boolean {
    if (!leaderboard.config.requireOptIn) {
      return true;
    }
    const isFeaturedLeaderboard = leaderboard.config.order === 1;
    if (isFeaturedLeaderboard) {
      return true;
    }
    return !this.calculateIfUserHasToOptedIn(leaderboard.config, user, true);
  }
}
